import { useId, type CSSProperties } from 'react';
import { AIQ_BASE, AIQ_MAX } from './learning-aiq';

export const folds = [
  'M146 52 C145 26 131 12 115 17 C103 12 91 20 86 30 C67 25 51 38 50 55 C31 59 26 78 34 93 C20 110 25 133 41 141 C39 161 58 179 77 177 C89 195 117 197 135 179 C144 168 146 157 146 151',
  'M129 32 C109 23 99 39 104 54 C86 46 70 56 72 74 C49 68 41 87 51 100',
  'M59 43 C65 53 62 64 53 69 M39 114 C56 105 70 114 68 131 C49 133 51 153 67 157',
  'M83 89 C64 87 60 101 68 113 M83 139 C69 149 78 167 91 165 C96 183 119 177 123 165',
  'M129 43 C120 56 126 64 117 72 M91 34 C88 45 90 53 99 61 M79 177 C73 163 78 151 88 151',
  'M45 91 C52 82 61 84 64 91 M94 70 C81 64 77 72 81 81 M101 154 C96 165 108 175 116 168',
  'M128 183 C130 192 136 197 143 200 M37 130 C47 133 54 128 54 120',
  'M115 22 C110 34 114 43 123 47 M136 34 C132 49 138 54 137 63 M105 180 C110 187 123 184 128 176',
  'M58 146 C65 140 75 140 81 145 M39 76 C48 74 55 77 56 84 M70 37 C70 46 76 52 84 50',
];

/** Decorative energy follows the measured index; it does not represent live processing. */
export function LearningAIQVisual({ score }: { score: number | null }) {
  const id = useId().replace(/:/g, '');
  const energy =
    score === null ? 0 : Math.max(0, Math.min(1, (score - AIQ_BASE) / (AIQ_MAX - AIQ_BASE)));
  const style = {
    '--aiq-energy': energy,
    '--aiq-cycle': `${6 - energy * 3.6}s`,
    '--aiq-light': 0.18 + energy * 0.82,
    '--aiq-pulse-low': 1 - energy * 0.025,
    '--aiq-pulse-high': 1.003 + energy * 0.027,
    '--aiq-spark-size': `${1.6 + energy * 1.6}px`,
  } as CSSProperties;
  return (
    <span className="aiq-organism" style={style} aria-hidden="true">
      <svg viewBox="0 0 300 220" className="aiq-neural-art">
        <defs>
          <radialGradient id={`${id}-aura`}>
            <stop stopColor="var(--cp-learning-art-12)" stopOpacity=".55" />
            <stop offset="1" stopColor="var(--cp-learning-art-13)" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${id}-ink`} x2=".8" y2="1">
            <stop stopColor="var(--cp-learning-art-14)" />
            <stop offset=".48" stopColor="var(--cp-learning-art-15)" />
            <stop offset="1" stopColor="var(--cp-learning-art-16)" />
          </linearGradient>
          <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        <ellipse
          className="aiq-aura"
          cx="150"
          cy="108"
          rx="146"
          ry="107"
          fill={`url(#${id}-aura)`}
        />
        {[false, true].map((mirror) => (
          <g
            key={String(mirror)}
            transform={mirror ? 'translate(300 0) scale(-1 1)' : undefined}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <g
              className="aiq-bloom"
              stroke="var(--cp-learning-art-17)"
              strokeWidth="3"
              filter={`url(#${id}-glow)`}
            >
              {folds.map((d, i) => (
                <path key={i} d={d} />
              ))}
            </g>
            <g stroke={`url(#${id}-ink)`} strokeWidth="1.35">
              {folds.map((d, i) => (
                <path key={i} d={d} />
              ))}
            </g>
            <g className="aiq-impulses" stroke="var(--cp-learning-art-18)" strokeWidth="2">
              {folds.slice(0, 4).map((d, i) => (
                <path key={i} d={d} pathLength="100" style={{ animationDelay: `${i * -0.9}s` }} />
              ))}
            </g>
            {[
              [53, 55],
              [72, 74],
              [68, 131],
              [91, 165],
              [104, 54],
              [59, 43],
              [83, 89],
            ].map(([cx, cy], i) => (
              <circle
                className="aiq-synapse"
                key={i}
                cx={cx}
                cy={cy}
                r="2.1"
                fill="var(--cp-learning-art-19)"
                style={{ animationDelay: `${i * -0.55}s` }}
              />
            ))}
          </g>
        ))}
        <circle
          className="aiq-core-ring"
          cx="150"
          cy="110"
          r="46"
          fill="none"
          stroke="var(--cp-learning-art-20)"
          strokeOpacity=".25"
          strokeWidth=".6"
        />
      </svg>
      <span className="aiq-core">
        <strong>{score ?? AIQ_BASE}</strong>
        <b>AIQ</b>
      </span>
    </span>
  );
}
